import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import {
  DEFAULT_ACCOUNT_DETAILS_VISIBILITY,
  hoursFormatMap,
  uiUserSettingsDateFormatMap,
} from '../data-access/consts/user-account-settings.const';
import { UserModels, UserSettingsModels } from '@share-models';
import { UserAccountSettingsSelectors } from '../data-access/state/user-account-settings.selector-types';
import {
  SharedActions,
  UserActions,
  UserSettingsActions,
} from '../data-access/state/user-account-settings.actions';
import { ChangeAutoLogoutModalComponent } from '../modals/change-auto-logout-modal/change-auto-logout-modal.component';
import { ChangeDevicePageViewModalComponent } from '../modals/change-device-page-view-modal/change-device-page-view-modal.component';
import { ChangeDeviceEmailsModalComponent } from '../modals/change-device-emails-modal/change-device-emails-modal.component';
import { ChangeUserDateSettingsModalComponent } from '../modals/change-user-date-settings-modal/change-user-date-settings-modal.component';
import { ChangeUserLanguageModalComponent } from '../modals/change-user-language-modal/change-user-language-modal.component';
import { ChangeUserNameModalComponent } from '../modals/change-user-name-modal/change-user-name-modal.component';
import { ChangeUserThemeModalComponent } from '../modals/change-user-theme-modal/change-user-theme-modal.component';
import { ChangeWeeklySubscriptionModalComponent } from '../modals/change-weekly-subscription-modal/change-weekly-subscription-modal.component';
import { GlobalSearchSettingsModalComponent } from '../modals/global-search-settings-modal/global-search-settings-modal.component';
import {
  MatSelectBottomSheetDirective,
  RouteModalService,
  UiBottomSheetComponent,
  UiButtonComponent,
  UiModels,
  UiPhoneInputV2Component,
  UiToggleComponent,
  UiTouchFeedbackDirective,
  ViewportService,
} from '@ui';
import { VerifyEmailModalComponent } from '../modals/verify-email-modal/verify-email-modal.component';
import { VerifyPhoneModalComponent } from '../modals/verify-phone-modal/verify-phone-modal.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import * as moment from 'moment-timezone';

type SheetType =
  | 'name'
  | 'email'
  | 'phone'
  | 'datetime'
  | 'language'
  | 'theme'
  | 'weekly'
  | 'device'
  | 'search'
  | 'logout'
  | null;

interface DialogResult {
  user?: UserModels.UserDocument;
  settings?: UserSettingsModels.UserSettingsDocument;
}

@Component({
  selector: 'share-user-account-details',
  templateUrl: './account-details.component.html',
  styleUrl: './account-details.component.scss',
  standalone: true,
  imports: [
    TranslatePipe,
    NgIf,
    NgTemplateOutlet,
    DatePipe,
    MatTooltip,
    UiTouchFeedbackDirective,
    UiBottomSheetComponent,
    UiButtonComponent,
    UiToggleComponent,
    UiPhoneInputV2Component,
    ReactiveFormsModule,
    MatRadioGroup,
    MatRadioButton,
    MatSelect,
    MatOption,
    MatSelectBottomSheetDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountDetailsComponent implements OnInit {
  private translateService = inject(TranslateService);
  private store$ = inject(Store);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private actions$ = inject(Actions);
  private routeModalService = inject(RouteModalService);
  public viewport = inject(ViewportService);
  private lastEmittedLang?: UserSettingsModels.LANGUAGE;

  @Input()
  public settingsVisibility?: Partial<UserSettingsModels.AccountDetailsVisibility>;
  @Input()
  public version: string | null = null;
  @Input()
  public enableActions = true;
  @Output()
  public userUpdated = new EventEmitter<UserModels.UserDocument>();
  @Output()
  public settingsUpdated =
    new EventEmitter<UserSettingsModels.UserSettingsDocument>();
  @Output()
  public languageChanged = new EventEmitter<UserSettingsModels.LANGUAGE>();

  @Output()
  public globalSearchSync = new EventEmitter<void>();
  @Output()
  public emailConfirmed = new EventEmitter<void>();

  @Input()
  public options: UserSettingsModels.AccountDetailsOptions | undefined;

  public userProfile = this.store$.selectSignal(
    UserAccountSettingsSelectors.userProfile,
  );
  public selectSelectedUserSettings = this.store$.selectSignal(
    UserAccountSettingsSelectors.selectUserSettings,
  );

  public dateTimeFormatMap = uiUserSettingsDateFormatMap;
  public timeFormatMap = hoursFormatMap;
  public langString = UserSettingsModels.langString;
  public themeString = UserSettingsModels.themeString;
  public readonly Setting = UserSettingsModels.AccountDetailsSetting;

  public activeSheet = signal<SheetType>(null);

  public languages = [
    { value: 'en', label: UserSettingsModels.langString.en },
    { value: 'es', label: UserSettingsModels.langString.es },
    { value: 'he', label: UserSettingsModels.langString.he },
  ];
  public themes = [
    { value: 'light', label: UserSettingsModels.themeString.light },
    { value: 'dark', label: UserSettingsModels.themeString.dark },
  ];
  public dateFormatOptions = Object.keys(uiUserSettingsDateFormatMap).map(
    (k) => ({
      value: +k as UserSettingsModels.UiUserSettingsDateFormat,
      name: uiUserSettingsDateFormatMap[
        +k as UserSettingsModels.UiUserSettingsDateFormat
      ],
    }),
  );
  public hourFormatEnum = { hours24: 0, hours12: 1 };
  public timeZones = moment.tz.names();

  public nameControl = new FormControl('');
  public languageControl = new FormControl('');
  public themeControl = new FormControl('');
  public devicePageViewControl = new FormControl(true);
  public dateTimeForm = new FormGroup({
    dateFormat: new FormControl(0),
    hoursFormat: new FormControl(0),
    timezone: new FormControl(''),
  });
  public weeklyControl = new FormControl(true);
  public autoLogoutForm = new FormGroup({
    enabled: new FormControl(false),
    minutes: new FormControl(60),
  });
  public searchForm = new FormGroup({
    markResult: new FormControl(true),
    includeAllMatch: new FormControl(true),
  });

  /* eslint-disable @typescript-eslint/unbound-method -- Angular Validators statics don't use `this` */
  public emailControl = new FormControl('', [
    Validators.required,
    Validators.email,
  ]);
  public phoneControl = new FormControl('', [Validators.required]);
  public confirmationCodeControl = new FormControl('', [
    Validators.required,
    Validators.pattern('^[0-9]*$'),
    Validators.minLength(6),
    Validators.maxLength(6),
  ]);
  /* eslint-enable @typescript-eslint/unbound-method */
  public verifyCodeSentId = signal<string | null>(null);
  public verifyResendTimer = signal(0);
  public isSaving = this.store$.selectSignal(
    UserAccountSettingsSelectors.selectIsSaving,
  );
  private resendInterval: ReturnType<typeof setInterval> | null = null;

  private readonly deviceEmailChannels: UserSettingsModels.DeviceEmailChannelOption[] = [
    {
      key: UserSettingsModels.DeviceChannel.CAMERA,
      label: 'SUAS.ACCOUNT.DETAILS.DEVICE_EMAILS.CAMERA_LABEL',
    },
    {
      key: UserSettingsModels.DeviceChannel.CORE,
      label: 'SUAS.ACCOUNT.DETAILS.DEVICE_EMAILS.CORE_LABEL',
    },
  ];
  private readonly deviceEmailFrequencyLabels: Record<UserSettingsModels.EmailFrequency, string> = {
    [UserSettingsModels.EmailFrequency.REAL_TIME]: 'SUAS.ACCOUNT.DETAILS.DEVICE_EMAILS.REAL_TIME',
    [UserSettingsModels.EmailFrequency.DAILY]: 'SUAS.ACCOUNT.DETAILS.DEVICE_EMAILS.DAILY',
    [UserSettingsModels.EmailFrequency.WEEKLY]: 'SUAS.ACCOUNT.DETAILS.DEVICE_EMAILS.WEEKLY',
  };

  private userEffect = effect(() => {
    const user = this.userProfile();
    if (user?._id) {
      this.userUpdated.emit(user);
    }
  });

  private settingsEffect = effect(() => {
    const settings = this.selectSelectedUserSettings();
    if (settings) {
      this.settingsUpdated.emit(settings);
      if (settings.lang) {
        this.updateLanguage(settings.lang);
      }
    }
  });

  public ngOnInit(): void {
    this.store$.dispatch(UserActions.loadUserProfile());
    this.store$.dispatch(UserSettingsActions.loadUserSettings());

    this.initModalListener();
    this.initVerificationListeners();
  }

  // ==========================================================================
  // ============================== Methods ===================================

  public initModalListener(): void {
    this.routeModalService.initModalListeners(
      [
        {
          queryParam: UiModels.openDialogByParams.changeEmail,
          open: () => this.openChangeEmailDialog(),
        },
        {
          queryParam: UiModels.openDialogByParams.changePhone,
          open: () => this.openChangePhoneDialog(),
        },
        {
          queryParam: UiModels.openDialogByParams.changeDateSettings,
          open: () => this.openChangeUserDateSettingsDialog(),
        },
        {
          queryParam: UiModels.openDialogByParams.changeAutoLogout,
          open: () => this.openChangeAutoLogoutDialog(),
        },
        {
          queryParam: UiModels.openDialogByParams.changeUsername,
          open: () => this.openChangeUsernameDialog(),
        },
        {
          queryParam: UiModels.openDialogByParams.changeLanguage,
          open: () => this.openChangeUserLanguageDialog(),
        },
        {
          queryParam: UiModels.openDialogByParams.changeTheme,
          open: () => this.openChangeUserThemeDialog(),
        },
        {
          queryParam: UiModels.openDialogByParams.changeWeeklySubscription,
          open: () => this.openChangeWeeklySubscriptionDialog(),
        },
        {
          queryParam: UiModels.openDialogByParams.changeDevicePageView,
          open: () => this.openChangeDevicePageViewDialog(),
        },
        {
          queryParam: UiModels.openDialogByParams.changeDeviceEmails,
          open: () => this.openChangeDeviceEmailsDialog(),
        },
        {
          queryParam: UiModels.openDialogByParams.globalSearchSettings,
          open: () => this.openGlobalSearchSettingsDialog(),
        },
      ],
      this.destroyRef,
    );
  }

  public isVisible(setting: UserSettingsModels.AccountDetailsSetting): boolean {
    return this.settingsVisibility?.[setting] ?? DEFAULT_ACCOUNT_DETAILS_VISIBILITY[setting];
  }

  public deviceEmailsSummary(): string {
    const orgDefault = this.translateLabel('SUAS.ACCOUNT.DETAILS.DEVICE_EMAILS.ORG_DEFAULT');
    if (this.deviceEmailChannels.every((channel) => this.deviceChannelFrequencies(channel.key).length === 0)) {
      return orgDefault;
    }
    return this.deviceEmailChannels
      .map((channel) => {
        const frequencies = this.deviceChannelFrequencies(channel.key);
        const value = frequencies.length
          ? frequencies
              .map((frequency) => this.translateLabel(this.deviceEmailFrequencyLabels[frequency]))
              .join(', ')
          : orgDefault;
        return `${this.translateLabel(channel.label)}: ${value}`;
      })
      .join(' · ');
  }

  private translateLabel(key: string): string {
    return String(this.translateService.instant(key));
  }

  public changeDeviceEmailsModal(): void {
    this.routeModalService.openModal(UiModels.openDialogByParams.changeDeviceEmails);
  }

  private deviceChannelFrequencies(
    channel: UserSettingsModels.DeviceChannel,
  ): UserSettingsModels.EmailFrequency[] {
    const value = this.selectSelectedUserSettings()?.deviceStability?.[channel];
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      return [value];
    }
    return [];
  }

  public changeUsernameModal(): void {
    this.routeModalService.openModal(
      UiModels.openDialogByParams.changeUsername,
    );
  }

  public changeEmailModal(): void {
    this.routeModalService.openModal(UiModels.openDialogByParams.changeEmail);
  }

  public changePhoneModal(): void {
    this.routeModalService.openModal(UiModels.openDialogByParams.changePhone);
  }

  public changeUserDateSettingsModal(): void {
    this.routeModalService.openModal(
      UiModels.openDialogByParams.changeDateSettings,
    );
  }

  public changeAutoLogoutModal(): void {
    this.routeModalService.openModal(
      UiModels.openDialogByParams.changeAutoLogout,
    );
  }

  public changeUserLanguageModal(): void {
    this.routeModalService.openModal(
      UiModels.openDialogByParams.changeLanguage,
    );
  }

  public changeUserThemeModal(): void {
    this.routeModalService.openModal(UiModels.openDialogByParams.changeTheme);
  }

  public changeWeeklySubscriptionModal(): void {
    this.routeModalService.openModal(
      UiModels.openDialogByParams.changeWeeklySubscription,
    );
  }

  public changeDevicePageViewDialog(): void {
    this.routeModalService.openModal(
      UiModels.openDialogByParams.changeDevicePageView,
    );
  }

  public changeGlobalSearchSettingsDialog(): void {
    this.routeModalService.openModal(
      UiModels.openDialogByParams.globalSearchSettings,
    );
  }

  public openSheet(sheet: SheetType): void {
    if (!this.enableActions) return;
    const user = this.userProfile();
    const settings = this.selectSelectedUserSettings();

    switch (sheet) {
      case 'name':
        this.nameControl.setValue(
          `${user?.firstname ?? ''} ${user?.lastname ?? ''}`.trim(),
        );
        break;
      case 'email':
        this.emailControl.reset('');
        this.confirmationCodeControl.reset('');
        this.verifyCodeSentId.set(null);
        this.verifyResendTimer.set(0);
        break;
      case 'phone':
        this.phoneControl.reset(user?.phone ?? '');
        this.confirmationCodeControl.reset('');
        this.verifyCodeSentId.set(null);
        this.verifyResendTimer.set(0);
        break;
      case 'language':
        this.languageControl.setValue(settings?.lang ?? 'en');
        break;
      case 'theme':
        this.themeControl.setValue(settings?.theme ?? 'dark');
        break;
      case 'device':
        this.devicePageViewControl.setValue(
          settings?.newDevicePageView !== false,
        );
        break;
      case 'datetime':
        this.dateTimeForm.setValue({
          dateFormat: settings?.dateFormat ?? 0,
          hoursFormat: settings?.hoursFormat ?? 0,
          timezone: user?.timezone ?? '',
        });
        break;
      case 'weekly':
        this.weeklyControl.setValue(settings?.subForWeekly ?? true);
        break;
      case 'logout':
        this.autoLogoutForm.setValue({
          enabled: settings?.autoLogoutEnabled ?? false,
          minutes: settings?.autoLogout ?? 60,
        });
        break;
      case 'search':
        this.searchForm.setValue({
          markResult: settings?.globalSearchSettings?.markResult ?? true,
          includeAllMatch:
            settings?.globalSearchSettings?.includeAllMatch ?? true,
        });
        break;
    }
    this.activeSheet.set(sheet);
  }

  public closeSheet(): void {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
      this.resendInterval = null;
    }
    this.activeSheet.set(null);
  }

  public saveLanguage(): void {
    const lang = this.languageControl.value;
    if (!lang) return;
    this.store$.dispatch(
      UserSettingsActions.startUpdateLanguage({
        lang: lang as UserSettingsModels.LANGUAGE,
      }),
    );
    this.closeSheet();
  }

  public saveTheme(): void {
    const theme = this.themeControl.value;
    if (!theme) return;
    this.store$.dispatch(
      UserSettingsActions.startUpdateTheme({
        theme: theme as UserSettingsModels.THEME,
      }),
    );
    this.closeSheet();
  }

  public saveDevicePageView(): void {
    this.store$.dispatch(
      UserSettingsActions.updateDevicePageView({
        newDevicePageView: !!this.devicePageViewControl.value,
      }),
    );
    this.closeSheet();
  }

  public saveName(): void {
    const fullName = (this.nameControl.value ?? '').trim();
    if (!fullName) return;
    const spaceIdx = fullName.indexOf(' ');
    const firstname =
      spaceIdx > -1 ? fullName.substring(0, spaceIdx) : fullName;
    const lastname = spaceIdx > -1 ? fullName.substring(spaceIdx + 1) : '';
    this.store$.dispatch(
      UserActions.startUpdateUserProfile({
        data: { firstname, lastname },
      }),
    );
    this.closeSheet();
  }

  public saveDateTime(): void {
    const v = this.dateTimeForm.value;
    const user = this.userProfile();
    this.store$.dispatch(
      UserSettingsActions.startUpdateUserSettingsProcess({
        userSettings: {
          dateFormat: v.dateFormat!,
          hoursFormat: v.hoursFormat!,
        },
      }),
    );
    if (v.timezone && v.timezone !== user?.timezone) {
      this.store$.dispatch(UserActions.setTimezone({ timezone: v.timezone }));
      this.store$.dispatch(
        UserActions.startUpdateUserProfile({
          data: { timezone: v.timezone },
        }),
      );
    }
    this.closeSheet();
  }

  public saveWeekly(): void {
    this.store$.dispatch(
      UserSettingsActions.startUpdateWeeklySubscription({
        subForWeekly: !!this.weeklyControl.value,
      }),
    );
    this.closeSheet();
  }

  public saveAutoLogout(): void {
    const v = this.autoLogoutForm.value;
    this.store$.dispatch(
      UserSettingsActions.startUpdateUserSettingsProcess({
        userSettings: {
          autoLogoutEnabled: !!v.enabled,
          autoLogout: +(v.minutes ?? 60),
        },
      }),
    );
    this.closeSheet();
  }

  public saveGlobalSearch(): void {
    const v = this.searchForm.value;
    this.store$.dispatch(
      UserSettingsActions.startUpdateGlobalSearchSettings({
        globalSearchSettings: {
          markResult: !!v.markResult,
          includeAllMatch: !!v.includeAllMatch,
        },
      }),
    );
    this.closeSheet();
  }

  public sendEmailCode(): void {
    const email = this.emailControl.value ?? '';
    if (!email) return;
    this.store$.dispatch(UserActions.startChangeEmailProcess({ email }));
  }

  public sendPhoneCode(): void {
    const phone = this.phoneControl.value ?? '';
    if (!phone) return;
    this.store$.dispatch(UserActions.startChangePhoneProcess({ phone }));
  }

  public confirmCode(): void {
    const code = this.confirmationCodeControl.value ?? '0';
    const id = this.verifyCodeSentId();
    if (!id) return;
    this.store$.dispatch(
      UserSettingsActions.startConfirmCode({ code: Number(code), id }),
    );
  }

  private initVerificationListeners(): void {
    this.actions$
      .pipe(
        ofType(UserActions.changeEmailRequestSuccess),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => {
        this.verifyCodeSentId.set(res.id);
        this.startResendTimer();
      });

    this.actions$
      .pipe(
        ofType(UserActions.changePhoneRequestSuccess),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => {
        this.verifyCodeSentId.set(res.id);
        this.startResendTimer();
      });

    this.actions$
      .pipe(
        ofType(UserSettingsActions.confirmCodeSuccess),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const sheet = this.activeSheet();
        if (sheet === 'email') {
          this.emailConfirmed.emit();
        }
        if (sheet === 'phone') {
          this.store$.dispatch(
            SharedActions.showMessage({
              success: String(
                this.translateService.instant('TOASTS.PHONE_CHANGED_SUCCESS'),
              ),
            }),
          );
        }
        this.closeSheet();
      });
  }

  private startResendTimer(): void {
    this.verifyResendTimer.set(60);
    if (this.resendInterval) clearInterval(this.resendInterval);
    this.resendInterval = setInterval(() => {
      const t = this.verifyResendTimer();
      if (t <= 1) {
        this.verifyResendTimer.set(0);
        if (this.resendInterval) {
          clearInterval(this.resendInterval);
          this.resendInterval = null;
        }
      } else {
        this.verifyResendTimer.set(t - 1);
      }
    }, 1000);
  }

  private updateLanguage(lang: UserSettingsModels.LANGUAGE): void {
    if (lang && (lang as string) !== this.translateService.getCurrentLang()) {
      this.translateService.use(lang);
    }
    if (lang !== this.lastEmittedLang) {
      this.lastEmittedLang = lang;
      this.languageChanged.emit(lang);
    }
  }

  // ==========================================================================
  // Actual dialog openers (used by RouteModalService listeners)

  private openChangeEmailDialog(): MatDialogRef<
    VerifyEmailModalComponent,
    DialogResult
  > {
    const user = this.userProfile();
    return this.dialog.open(VerifyEmailModalComponent, {
      width: '572px',
      data: { user: user ?? undefined, onEmailConfirmed: () => this.emailConfirmed.emit() },
    });
  }

  private openChangePhoneDialog(): MatDialogRef<
    VerifyPhoneModalComponent,
    DialogResult
  > {
    const user = this.userProfile();
    return this.dialog.open(VerifyPhoneModalComponent, {
      width: '572px',
      height: '450px',
      data: {
        phone: user?.phone ?? '',
        isVerified: user?.isPhoneVerified ?? false,
      },
    });
  }

  private openChangeUserDateSettingsDialog(): MatDialogRef<
    ChangeUserDateSettingsModalComponent,
    DialogResult
  > {
    const user = this.userProfile();
    const settings = this.selectSelectedUserSettings();
    return this.dialog.open(ChangeUserDateSettingsModalComponent, {
      width: '572px',
      data: user && settings ? { user, settings } : undefined,
    });
  }

  private openChangeAutoLogoutDialog(): MatDialogRef<
    ChangeAutoLogoutModalComponent,
    DialogResult
  > {
    const settings = this.selectSelectedUserSettings();
    return this.dialog.open(ChangeAutoLogoutModalComponent, {
      width: '572px',
      data: settings ? { settings } : undefined,
    });
  }

  private openChangeUsernameDialog(): MatDialogRef<
    ChangeUserNameModalComponent,
    DialogResult
  > {
    const user = this.userProfile();
    return this.dialog.open(ChangeUserNameModalComponent, {
      width: '572px',
      data: user ? { user } : undefined,
    });
  }

  private openChangeUserLanguageDialog(): MatDialogRef<
    ChangeUserLanguageModalComponent,
    DialogResult
  > {
    const settings = this.selectSelectedUserSettings();
    return this.dialog.open(ChangeUserLanguageModalComponent, {
      width: '572px',
      data: settings ? { settings } : undefined,
    });
  }

  private openChangeUserThemeDialog(): MatDialogRef<
    ChangeUserThemeModalComponent,
    DialogResult
  > {
    const settings = this.selectSelectedUserSettings();
    return this.dialog.open(ChangeUserThemeModalComponent, {
      width: '572px',
      data: settings ? { settings } : undefined,
    });
  }

  private openChangeWeeklySubscriptionDialog(): MatDialogRef<
    ChangeWeeklySubscriptionModalComponent,
    DialogResult
  > {
    const settings = this.selectSelectedUserSettings();
    return this.dialog.open(ChangeWeeklySubscriptionModalComponent, {
      width: '572px',
      data: settings ? { settings } : undefined,
    });
  }

  private openChangeDevicePageViewDialog(): MatDialogRef<
    ChangeDevicePageViewModalComponent,
    DialogResult
  > {
    const settings = this.selectSelectedUserSettings();
    return this.dialog.open(ChangeDevicePageViewModalComponent, {
      width: '572px',
      data: settings ? { userSettings: settings } : undefined,
    });
  }

  private openChangeDeviceEmailsDialog(): MatDialogRef<
    ChangeDeviceEmailsModalComponent,
    DialogResult
  > {
    const settings = this.selectSelectedUserSettings();
    return this.dialog.open(ChangeDeviceEmailsModalComponent, {
      width: '572px',
      data: settings ? { settings } : undefined,
    });
  }

  private openGlobalSearchSettingsDialog(): MatDialogRef<
    GlobalSearchSettingsModalComponent,
    DialogResult
  > {
    const settings = this.selectSelectedUserSettings();
    return this.dialog.open(GlobalSearchSettingsModalComponent, {
      width: '572px',
      data: settings
        ? {
            globalSearchSettings: settings.globalSearchSettings,
          }
        : undefined,
    });
  }

  public sync(): void {
    this.globalSearchSync.emit();
  }
}
